import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Button,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  Checkbox,
  FormControlLabel,
  Switch,
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import MainLayout from '../../components/layout/MainLayout';
import axios from 'axios';

interface MastersListProps {
  masterType: string;
  masterLabel: string;
  hasDefault?: boolean;
}

const MastersList: React.FC<MastersListProps> = ({ masterType, masterLabel, hasDefault = false }) => {
  const [items, setItems] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<any>(null);
  const [formData, setFormData] = useState({ name: '', code: '', description: '', isDefault: false });

  useEffect(() => {
    fetchItems();
  }, [masterType]);

  const fetchItems = async () => {
    try {
      const response = await axios.get(`/system-settings/masters/${masterType}`);
      setItems(response.data);
    } catch (error) {
      console.error(`Failed to fetch ${masterLabel}:`, error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingItem(null);
    setFormData({ name: '', code: '', description: '', isDefault: false });
    setDialogOpen(true);
  };

  const handleEdit = (item: any) => {
    setEditingItem(item);
    setFormData({
      name: item.name,
      code: item.code,
      description: item.description || '',
      isDefault: Boolean(item.isDefault),
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    try {
      const payload: any = { ...formData };
      if (!hasDefault) {
        delete payload.isDefault;
      }

      // List of masters that DO NOT support 'description' field in backend/Prisma
      const noDescriptionMasters = [
        'blood-group',
        'caste',
        'category',
        'religion',
        'nationality',
        'mother-tongue',
        'motherTongue',
        'family-relation',
        'familyRelation',
        'child-status',
        'childStatus',
        'bank'
      ];

      if (noDescriptionMasters.includes(masterType)) {
        delete payload.description;
      }

      if (editingItem) {
        await axios.patch(`/system-settings/masters/${masterType}/${editingItem.id}`, payload);
      } else {
        await axios.post(`/system-settings/masters/${masterType}`, payload);
      }
      setDialogOpen(false);
      fetchItems();
    } catch (error: any) {
      alert(error.response?.data?.message || `Failed to save ${masterLabel}`);
    }
  };

  const handleToggleStatus = async (id: string, currentStatus: boolean) => {
    try {
      await axios.patch(`/system-settings/masters/${masterType}/${id}/toggle-status`);
      setItems((prev) =>
        prev.map((item) =>
          item.id === id ? { ...item, isActive: !currentStatus } : item
        )
      );
    } catch (error: any) {
      alert(error.response?.data?.message || 'Failed to toggle status');
      fetchItems();
    }
  };

  const handleDelete = async (id: string) => {
    if (window.confirm(`Are you sure you want to delete this ${masterLabel}?`)) {
      try {
        await axios.delete(`/system-settings/masters/${masterType}/${id}`);
        fetchItems();
      } catch (error: any) {
        alert(error.response?.data?.message || `Failed to delete ${masterLabel}`);
      }
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6">{masterLabel}</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={handleCreate}>
          New {masterLabel}
        </Button>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Code</TableCell>
              {hasDefault && <TableCell>Default</TableCell>}
              <TableCell>Description</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {items.length === 0 ? (
              <TableRow>
                <TableCell colSpan={hasDefault ? 6 : 5} align="center">
                  <Typography variant="body2" color="textSecondary" sx={{ py: 3 }}>
                    No {masterLabel.toLowerCase()} found. Create your first one to get started.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              items.map((item) => (
                <TableRow key={item.id}>
                  <TableCell>{item.name}</TableCell>
                  <TableCell>{item.code}</TableCell>
                  {hasDefault && (
                    <TableCell>
                      <Chip
                        label={item.isDefault ? 'Default' : ''}
                        color={item.isDefault ? 'primary' : 'default'}
                        size="small"
                        sx={{ visibility: item.isDefault ? 'visible' : 'hidden' }}
                      />
                    </TableCell>
                  )}
                  <TableCell>{item.description || '-'}</TableCell>
                  <TableCell>
                    <Switch
                      checked={item.isActive}
                      onChange={() => handleToggleStatus(item.id, item.isActive)}
                      color="primary"
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <IconButton size="small" onClick={() => handleEdit(item)}>
                      <EditIcon />
                    </IconButton>
                    <IconButton size="small" onClick={() => handleDelete(item.id)}>
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingItem ? `Edit ${masterLabel}` : `Create ${masterLabel}`}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              fullWidth
              required
            />
            <TextField
              label="Code"
              value={formData.code}
              onChange={(e) => setFormData({ ...formData, code: e.target.value })}
              fullWidth
              required
            />
            <TextField
              label="Description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              fullWidth
              multiline
              rows={3}
            />
            {hasDefault && (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.isDefault}
                    onChange={(e) => setFormData({ ...formData, isDefault: e.target.checked })}
                  />
                }
                label="Set as Default"
              />
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSave} variant="contained">
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MastersList;

